import { useMutation } from "@apollo/client/react";
import { alert } from "@components/common/Alert";
import { MarkdownContent } from "@components/design-system/atoms/MarkdownContent";
import {
  DSButton,
  DSCardWrapper,
  DSInput,
  DSRichTextarea,
} from "@components/design-system/molecules";
import { faAddressBook } from "@fortawesome/free-regular-svg-icons";
import {
  faBirthdayCake,
  faBroadcastTower,
  faHandScissors,
  faMicrochip,
  faPalette,
  faPaw,
  faPen,
} from "@fortawesome/free-solid-svg-icons";
import { faThumbTack } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import React, { useState } from "react";
import { useNavigate } from "react-router";

import { getAllKeyInfo, filterSummaryData } from "./utils";
import Twemoji from "../../../Twemoji";
import { EDIT_PATIENT_MUTATION } from "../../patients/api/mutations";
import {
  formatPatientAge,
  species as speciesMap,
} from "../../patients/utils";

function ReadOnlyStat({
  icon,
  emoji,
  label,
  text,
  onEdit,
  dataTestId,
}) {
  return (
    <button
      type="button"
      onClick={onEdit}
      title="Edit in Patient form"
      data-testid={dataTestId}
      className="group flex flex-row items-center gap-sm w-full text-left rounded-standard px-xs py-xs hover:bg-tertiary transition-colors cursor-pointer"
    >
      <span className="text-secondary shrink-0 w-4 text-center">
        {icon ? (
          <FontAwesomeIcon icon={icon} />
        ) : emoji ? (
          <Twemoji emoji={emoji} />
        ) : null}
      </span>
      <div className="flex flex-col flex-1 min-w-0">
        {label ? (
          <span className="text-xs font-bold text-primary">
            {label}
          </span>
        ) : null}
        <span
          className={`text-xs wrap-break-word ${
            text ? "text-primary" : "text-placeholder italic"
          }`}
        >
          {text || "Not recorded"}
        </span>
      </div>
      <FontAwesomeIcon
        icon={faPen}
        className="text-placeholder opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
      />
    </button>
  );
}

function buildSexLabel(sex, isIntact) {
  const sexWord =
    sex === "f" ? "Female" : sex === "m" ? "Male" : null;
  if (!sexWord) return "";
  const status = isIntact
    ? "Intact"
    : sex === "f"
      ? "Spayed"
      : "Neutered";
  return `${status} ${sexWord}`;
}

function PatientStats({ patient, onOpenEdit }) {
  const sexLabel = buildSexLabel(patient.sex, patient.isIntact);
  const speciesEntry = patient.species
    ? speciesMap[patient.species]
    : null;
  const speciesText = speciesEntry?.displayName || patient.species;
  // Empty so the ReadOnlyStat renders its own italic "Not recorded", matching
  // the sibling fields.
  const ageText = formatPatientAge(patient.age, "");

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-md gap-y-xs pb-md">
      <ReadOnlyStat
        emoji={speciesEntry?.icon}
        icon={speciesEntry ? null : faPaw}
        label="Species"
        text={speciesText}
        onEdit={onOpenEdit}
        dataTestId="patientStat-species"
      />
      <ReadOnlyStat
        icon={faPaw}
        label="Breed"
        text={patient.breed}
        onEdit={onOpenEdit}
        dataTestId="patientStat-breed"
      />
      <ReadOnlyStat
        icon={faPalette}
        label="Color"
        text={patient.color}
        onEdit={onOpenEdit}
        dataTestId="patientStat-color"
      />
      <ReadOnlyStat
        icon={faBirthdayCake}
        label="Age"
        text={ageText}
        onEdit={onOpenEdit}
        dataTestId="patientStat-age"
      />
      <ReadOnlyStat
        icon={faHandScissors}
        label="Sex"
        text={sexLabel}
        onEdit={onOpenEdit}
        dataTestId="patientStat-sex"
      />
      <ReadOnlyStat
        icon={faMicrochip}
        label="Microchip"
        text={patient.microchip}
        onEdit={onOpenEdit}
        dataTestId="patientStat-microchip"
      />
      <ReadOnlyStat
        icon={faAddressBook}
        label="Owner Contact"
        text={patient.ownerContactInfo}
        onEdit={onOpenEdit}
        dataTestId="patientStat-ownerContact"
      />
      <ReadOnlyStat
        icon={faBroadcastTower}
        label="Emergency Contact"
        text={patient.emergencyContactInfo}
        onEdit={onOpenEdit}
        dataTestId="patientStat-emergencyContact"
      />
    </div>
  );
}

function InlinePinnedKeyInfo({
  fieldName,
  label,
  value,
  patientUUID,
  onSaved,
  multiline = true,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [editPatient, { loading }] = useMutation(
    EDIT_PATIENT_MUTATION,
  );

  function startEdit() {
    setDraft(value ?? "");
    setIsEditing(true);
  }

  async function save() {
    try {
      await editPatient({
        variables: {
          patientUuid: patientUUID,
          patientInput: { [fieldName]: draft || null },
        },
      });
      setIsEditing(false);
      onSaved?.();
    } catch {
      alert(
        "error",
        `Failed to save ${label.toLowerCase()}. Please try again.`,
      );
    }
  }

  function cancel() {
    setDraft(value ?? "");
    setIsEditing(false);
  }

  if (isEditing) {
    return (
      <div className="bg-brand-subtle rounded-standard mx-sm mb-sm p-sm">
        {multiline ? (
          <DSRichTextarea
            value={draft}
            onChange={(content) => setDraft(content)}
            placeholder={`Add pinned ${label.toLowerCase()}...`}
            dataTestId={`${fieldName}-inline-edit`}
          >
            <DSRichTextarea.Input minHeight="80px" />
          </DSRichTextarea>
        ) : (
          <DSInput
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                save();
              }
              if (e.key === "Escape") cancel();
            }}
            autoFocus
            placeholder={`Add pinned ${label.toLowerCase()}...`}
            disabled={loading}
            dataTestId={`${fieldName}-inline-edit`}
          />
        )}
        <div className="flex justify-end gap-sm mt-sm">
          <DSButton
            variant="cancel"
            size="sm"
            onClick={cancel}
            dataTestId={`${fieldName}-inline-cancel`}
          >
            Cancel
          </DSButton>
          <DSButton
            variant="primary"
            size="sm"
            onClick={save}
            disabled={loading}
            dataTestId={`${fieldName}-inline-save`}
          >
            Save
          </DSButton>
        </div>
      </div>
    );
  }

  if (value) {
    return (
      <DSCardWrapper variant="info">
        <div className="group flex flex-row gap-sm">
          <FontAwesomeIcon icon={faThumbTack} className="shrink-0" />
          {multiline ? (
            <MarkdownContent
              content={value}
              className="text-xs leading-5 text-primary flex-1"
            />
          ) : (
            <span className="text-xs leading-5 text-primary flex-1">
              {value}
            </span>
          )}
          <button
            type="button"
            onClick={startEdit}
            aria-label={`Edit pinned ${label}`}
            title={`Edit pinned ${label}`}
            className="text-secondary hover:text-primary opacity-60 group-hover:opacity-100 transition-opacity cursor-pointer shrink-0"
          >
            <FontAwesomeIcon icon={faPen} />
          </button>
        </div>
      </DSCardWrapper>
    );
  }

  return (
    <DSCardWrapper
      variant="ghost"
      interactive
      onClick={startEdit}
      data-testid={`${fieldName}-inline-add`}
    >
      <div className="text-xs">
        <FontAwesomeIcon icon={faThumbTack} className="mr-1" />+ Add{" "}
        {label.toLowerCase()}
      </div>
    </DSCardWrapper>
  );
}

function KeyInfoColumn({
  emoji,
  title,
  fieldName,
  patientText,
  patientUUID,
  onSaved,
  renderKeyInformation,
  multiline = true,
}) {
  return (
    <div>
      <h6 className="font-semibold text-secondary pb-sm">
        <Twemoji emoji={emoji} /> {title}
      </h6>
      <div className="max-h-60 overflow-y-auto">
        <InlinePinnedKeyInfo
          fieldName={fieldName}
          label={title}
          value={patientText}
          patientUUID={patientUUID}
          onSaved={onSaved}
          multiline={multiline}
        />
        {renderKeyInformation()}
      </div>
    </div>
  );
}

function SummaryItem({ date = null, text, noteUuid = null }) {
  const navigate = useNavigate();

  return (
    <button
      type="button"
      title="Click to go to source note"
      onClick={() => navigate(`/edit_note/${noteUuid}`)}
      className="w-full flex flex-row items-start gap-sm px-md py-sm text-left hover:bg-tertiary rounded-large transition-colors cursor-pointer"
    >
      <span className="flex-1 text-xs text-primary whitespace-pre-wrap">
        {text}
      </span>
      {date ? (
        <span className="text-xs text-placeholder">
          {moment(date).format("MM/DD/YY")}
        </span>
      ) : null}
    </button>
  );
}

export function Signalment({ patient, onOpenEdit }) {
  if (!patient) return null;
  return <PatientStats patient={patient} onOpenEdit={onOpenEdit} />;
}

export function KeyHistory({ patient, patientUUID, onSaved }) {
  if (!patient) return null;

  const {
    allKeyHistories,
    allKeyMedications,
    allKeyVaccinations,
    allWeights,
  } = getAllKeyInfo({ patient });

  const patientWeight =
    patient.weight && patient.weight !== "Not recorded"
      ? patient.weight
      : "";

  function renderKeyInformation(allKeyInformation, patientKeyInfo) {
    const filteredKeyInformation =
      filterSummaryData(allKeyInformation);

    if (filteredKeyInformation && filteredKeyInformation.length > 0) {
      return (
        <div>
          {filteredKeyInformation.map(
            ({ noteUuid, text, createdAt }, index) => (
              <SummaryItem
                key={index}
                noteUuid={noteUuid}
                text={text}
                date={createdAt}
              />
            ),
          )}
        </div>
      );
    }

    if (!patientKeyInfo) return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 auto-grid-rows gap-md">
      <KeyInfoColumn
        emoji="🩺"
        title="Key Medical History"
        fieldName="keyHistory"
        patientText={patient.keyHistory}
        patientUUID={patientUUID}
        onSaved={onSaved}
        renderKeyInformation={() =>
          renderKeyInformation(allKeyHistories, patient.keyHistory)
        }
      />
      <KeyInfoColumn
        emoji="💊"
        title="Chronic Medications"
        fieldName="keyMedications"
        patientText={patient.keyMedications}
        patientUUID={patientUUID}
        onSaved={onSaved}
        renderKeyInformation={() =>
          renderKeyInformation(
            allKeyMedications,
            patient.keyMedications,
          )
        }
      />
      <KeyInfoColumn
        emoji="💉"
        title="Immunization Record"
        fieldName="vaccinationStatus"
        patientText={patient.vaccinationStatus}
        patientUUID={patientUUID}
        onSaved={onSaved}
        renderKeyInformation={() =>
          renderKeyInformation(
            allKeyVaccinations,
            patient.vaccinationStatus,
          )
        }
      />
      <KeyInfoColumn
        emoji="⚖️"
        title="Weight"
        fieldName="weight"
        patientText={patientWeight}
        patientUUID={patientUUID}
        onSaved={onSaved}
        multiline={false}
        renderKeyInformation={() =>
          renderKeyInformation(allWeights, patientWeight)
        }
      />
    </div>
  );
}
